import { Box, styled, Tooltip, Typography } from '@mui/material';
import { IconAccessTime } from 'assets/img';
import { EmploymentType } from '@types';
import type { LevelTableCellProps } from 'redesign';
import { TableShiftPlanningCellProp } from 'ogp/components/views/planning/planning/content/table/shift-planning-table.types';
import { HqShiftPlanningCellProps } from '../shifts/table/shift-planning-table.types';
import { HqProjectPlanningCellProp } from '../../../../hq/components/views/planner/projects/table/project-planning-table.types';

type ValueCellProps<Props extends { value: unknown }> = LevelTableCellProps<object, Props['value']>;

const BoxDateTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
}));
const BoxTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  gap: theme.spacing(0.5),
}));
const BoxState = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'left',
}));
const BoxApplicationsNumbers = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'left',
  minWidth: theme.spacing(4),
}));
const BoxClaimantsLength = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'left',
  minWidth: theme.spacing(4),
}));

export const BoxAction = styled(Box)(() => ({
  display: 'flex',
}));
export const BoxIcon = styled(Box)(() => ({
  padding: '0.5em',
  borderRadius: '50%',
  transition: 'background-color 0.25s',
  '&:hover': {
    backgroundColor: '#D9D9D9',
    cursor: 'pointer',
  },
}));

export const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" color="secondary" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

export const JobNameCell = (props: ValueCellProps<HqShiftPlanningCellProps<'jobName'>>) => {
  return (
    <Tooltip title={props.value}>
      <Typography variant="body1" noWrap style={{ fontWeight: 'bold' }}>
        {props.value}
      </Typography>
    </Tooltip>
  );
};

export const Cell = (
  props: ValueCellProps<
    | HqShiftPlanningCellProps<'companyName' | 'departmentName' | 'location'>
    | HqProjectPlanningCellProp<'companyName' | 'departmentName'>
  >
) => {
  return (
    <Tooltip title={props.value}>
      <Typography variant="body1" noWrap>
        {props.value}
      </Typography>
    </Tooltip>
  );
};

export const CandidatePlacementsCell = (
  props: ValueCellProps<
    | HqShiftPlanningCellProps<'candidatePlacements'>
    | HqProjectPlanningCellProp<'candidatePlacements'>
    | TableShiftPlanningCellProp<'state'>
  >
): JSX.Element => {
  return (
    <BoxState>
      <BoxClaimantsLength>
        <Typography variant="body1" data-testid="claimants">
          {props.value.claimants.length}
        </Typography>
        <Typography variant="body1" color="secondary" data-testid="slash-maxclaimants">
          {'/' + props.value.maxClaimants}
        </Typography>
      </BoxClaimantsLength>
      {props.children}
    </BoxState>
  );
};

export const ApplicationsCell = (
  props: ValueCellProps<
    HqShiftPlanningCellProps<'applications'> | HqProjectPlanningCellProp<'applications'>
  >
): JSX.Element => {
  const numberOfTempWorkers = props.value.tempworkersList.length;
  const numberOfFreelancers = props.value.freelancersList.length;
  const numberOfEmployees = props.value.employeesList?.length;
  return (
    <BoxState>
      <BoxApplicationsNumbers>
        <Typography variant="body1" data-testid="claimants">
          {numberOfFreelancers} Freelancers
        </Typography>
        <Typography variant="body1" data-testid="claimants">
          {numberOfTempWorkers} Uitzendkrachten
        </Typography>
        {numberOfEmployees !== undefined && (
          <Typography variant="body1" data-testid="claimants">
            {numberOfEmployees} Werknemers
          </Typography>
        )}
      </BoxApplicationsNumbers>
      {props.children}
    </BoxState>
  );
};

export const DateTimeCell = (props: ValueCellProps<HqShiftPlanningCellProps<'dateTime'>>) => {
  return (
    <BoxDateTime>
      <Typography variant="body1" color="secondary">
        {props.value.fromDateTime.toFormat('dd MMM yyyy')}
      </Typography>
      <BoxTime>
        <IconAccessTime />
        <Typography variant="body1">
          {props.value.fromDateTime.toFormat('HH:mm') +
            '-' +
            props.value.toDateTime.toFormat('HH:mm')}
        </Typography>
      </BoxTime>
    </BoxDateTime>
  );
};

export const StartDateCell = (props: ValueCellProps<HqProjectPlanningCellProp<'fromDate'>>) => {
  return (
    <BoxDateTime>
      <Typography variant="body1" color="secondary">
        {props.value.toFormat('dd MMM yyyy')}
      </Typography>
    </BoxDateTime>
  );
};

export const EndDateCell = (props: ValueCellProps<HqProjectPlanningCellProp<'toDate'>>) => {
  return (
    <BoxDateTime>
      <Typography variant="body1" color="secondary">
        {props.value.toFormat('dd MMM yyyy')}
      </Typography>
    </BoxDateTime>
  );
};

export const getAllowedEmploymentTypes = (
  isFreelance: boolean,
  isTempWork: boolean,
  isEmployee: boolean
): EmploymentType[] => {
  const allowedEmploymentTypes = [];
  if (isFreelance) {
    allowedEmploymentTypes.push(EmploymentType.FREELANCER);
  }
  if (isTempWork) {
    allowedEmploymentTypes.push(EmploymentType.TEMPWORKER);
  }
  if (isEmployee) {
    allowedEmploymentTypes.push(EmploymentType.EMPLOYEE);
  }
  return allowedEmploymentTypes;
};
